import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

const LIMIT = 10;

/* ---------------- UI Bits ---------------- */

function Spinner({ label = "Loading..." }) {
  return (
    <div className="flex items-center justify-center py-6" role="status" aria-live="polite">
      <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
      <span className="sr-only">{label}</span>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="p-[1px] rounded-2xl bg-gradient-to-br from-sky-400/60 via-cyan-400/50 to-indigo-500/50">
      <div className="rounded-2xl bg-white/80 backdrop-blur-sm p-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-slate-200 animate-pulse" />
          <div className="flex-1 space-y-2">
            <div className="h-3 w-32 bg-slate-200 rounded animate-pulse" />
            <div className="h-3 w-20 bg-slate-200 rounded animate-pulse" />
          </div>
          <div className="h-5 w-12 bg-slate-200 rounded animate-pulse" />
        </div>
        <div className="mt-3 space-y-2">
          <div className="h-3 w-full bg-slate-200 rounded animate-pulse" />
          <div className="h-3 w-11/12 bg-slate-200 rounded animate-pulse" />
          <div className="h-3 w-9/12 bg-slate-200 rounded animate-pulse" />
        </div>
      </div>
    </div>
  );
}

function InitialAvatar({ name = "" }) {
  const initials = name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  // Soft randomized hue based on name hash for subtle variety
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
  const hue = Math.abs(hash) % 360;

  return (
    <div
      className="grid h-10 w-10 place-items-center rounded-full text-white font-semibold shadow-sm"
      style={{ background: `linear-gradient(135deg, hsl(${hue} 90% 55%), hsl(${(hue + 40) % 360} 90% 45%))` }}
      aria-hidden="true"
    >
      {initials || "?"}
    </div>
  );
}

function RatingStars({ value = 0 }) {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  return (
    <div className="flex items-center gap-1" aria-label={`Rating ${v} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          className={i < v ? "text-amber-500" : "text-slate-300"}
          aria-hidden="true"
        >
          ★
        </span>
      ))}
    </div>
  );
}

/* --------------- Main Component --------------- */

export default function ReviewList({ refresh }) {
  const [reviews, setReviews] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const containerRef = useRef(null);
  const sentinelRef = useRef(null);
  const lastRequestedPageRef = useRef(null); // guard against double requests of the same page

  // Reset when `refresh` changes
  useEffect(() => {
    setReviews([]);
    setPage(1);
    setHasMore(true);
    setError("");
    lastRequestedPageRef.current = null;
  }, [refresh]);

  // Fetch current page
  useEffect(() => {
    if (lastRequestedPageRef.current === page) return;
    lastRequestedPageRef.current = page;

    let cancelled = false;
    const controller = new AbortController();

    const fetchPage = async () => {
      if (!hasMore) return;
      setLoading(true);
      setError("");

      try {
        // If you’re proxying in dev (CRA/Vite), you can use "/api/reviews" instead.
        const { data } = await axios.get("http://localhost:5001/api/reviews", {
          params: { page, limit: LIMIT },
          signal: controller.signal,
        });

        const items = data?.items || [];
        const nextHasMore =
          typeof data?.hasMore === "boolean" ? data.hasMore : items.length === LIMIT;

        if (!cancelled) {
          setReviews((prev) => {
            if (items.length === 0) return prev;
            const seen = new Set(prev.map((r) => r._id));
            const merged = [...prev];
            for (const r of items) if (!seen.has(r._id)) merged.push(r);
            return merged;
          });
          setHasMore(nextHasMore);
        }
      } catch (e) {
        if (!cancelled && e.name !== "CanceledError") {
          setError("Failed to load reviews.");
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPage();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [page, hasMore]);

  // IntersectionObserver to paginate
  useEffect(() => {
    if (!hasMore) return;
    const rootEl = containerRef.current;
    const target = sentinelRef.current;
    if (!rootEl || !target) return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setPage((p) => p + 1);
      },
      { root: rootEl, rootMargin: "220px", threshold: 0.01 }
    );

    io.observe(target);
    return () => io.disconnect();
  }, [hasMore]);

  const isInitial = reviews.length === 0 && loading;

  return (
    <div className="mx-auto w-full max-w-3xl px-3 sm:px-4 lg:px-0">
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">
            What people say
          </h2>
          <p className="text-sm text-slate-600">
            Real feedback from our participants and partners.
          </p>
        </div>
        <div className="text-sm text-slate-500">
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 ring-1 ring-slate-200 ">
            <span className="h-2 w-2 rounded-full bg-emerald-500 " aria-hidden="true" />
            <span className="hidden">Infinite scroll enabled</span>
          </span>
        </div>
      </div>

      {/* Scrollable list container */}
      <div
        ref={containerRef}
        className="relative overflow-auto h-[70vh] md:h-[65vh] rounded-2xl p-4 sm:p-5 bg-gradient-to-br from-[#E7F7FC] to-white border border-slate-200 shadow-sm [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden "
        aria-busy={loading ? "true" : "false"}
        aria-live="polite"
        style={{paddingTop: 0}}
      >
        {/* Sticky top bar inside the scroller */}
        <div className="sticky top-0 z-10 -mx-4 sm:-mx-5 mb-3 p-3 sm:p-4 bg-gradient-to-b from-white/80 to-white/40 backdrop-blur supports-[backdrop-filter]:backdrop-blur rounded-t-2xl border-b border-white/50">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">
              {reviews.length ? `${reviews.length} review${reviews.length > 1 ? "s" : ""}` : "Loading…"}
            </span>
            {/* Average (quick visual) */}
            {!!reviews.length && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-amber-500">★</span>
                <span className="text-slate-700">
                  {(
                    reviews.reduce((a, b) => a + Number(b.rating || 0), 0) / reviews.length
                  ).toFixed(1)}
                  /5
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Initial skeletons */}
        {isInitial && (
          <div className="space-y-4">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        )}

        {/* Reviews */}
        {!isInitial && (
          <ul className="space-y-4">
            {reviews.map((r) => (
              <li key={r._id}>
                {/* Gradient ring card */}
                <div className="p-[1px] rounded-2xl bg-gradient-to-br from-sky-400/70 via-cyan-400/70 to-indigo-500/70 shadow-[0_10px_30px_-12px_rgba(16,24,40,0.2)]">
                  <article className="rounded-2xl bg-white/85 backdrop-blur-sm p-4 sm:p-5">
                    <div className="flex flex-col sm:flex-row sm:items-start gap-3 sm:gap-4">
                      <InitialAvatar name={r.name} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
                          <h4 className="text-slate-900 font-semibold truncate">{r.name}</h4>
                          <div className="flex items-center gap-2 shrink-0">
                            <RatingStars value={r.rating} />
                            <span className="text-xs text-slate-500">
                              {Number(r.rating).toFixed(1)}
                            </span>
                          </div>
                        </div>

                        <p className="mt-2 text-slate-700 text-sm leading-6">
                          {r.comment}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          {/* {r.email && (
                            <span className="inline-flex items-center gap-1">
                              <span className="h-1.5 w-1.5 rounded-full bg-sky-400" aria-hidden="true" />
                              {r.email}
                            </span>
                          )} */}
                          {r.createdAt && (
                            <span className="inline-flex items-center gap-1">
                              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden="true" />
                              {new Date(r.createdAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </article>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 rounded-lg bg-red-50 text-red-700 px-4 py-3 ring-1 ring-red-200 text-sm">
            {error}
          </div>
        )}

        {/* Paging spinner (after first batch) */}
        {reviews.length > 0 && loading && <Spinner label="Loading more reviews..." />}

        {/* End message */}
        {!hasMore && reviews.length > 0 && !loading && (
          <p className="mt-4 text-center text-slate-600 text-sm">— You’ve reached the end —</p>
        )}

        {/* Sentinel for IntersectionObserver */}
        <div ref={sentinelRef} className="h-1" />
      </div>
    </div>
  );
}























// ===================================================================================================================
// import { useEffect, useRef, useState } from "react";
// import axios from "axios";

// const LIMIT = 10;

// function Spinner({ label = "Loading..." }) {
//   return (
//     <div className="flex items-center justify-center py-4" role="status" aria-live="polite">
//       <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
//         <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
//         <path className="opacity-75" d="M4 12a8 8 0 0 1 8-8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
//       </svg>
//       <span className="sr-only">{label}</span>
//     </div>
//   );
// }

// export default function ReviewList({ refresh }) {
//   const [reviews, setReviews] = useState([]);
//   const [page, setPage] = useState(1);
//   const [hasMore, setHasMore] = useState(true);
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   const containerRef = useRef(null);
//   const sentinelRef = useRef(null);
//   const lastRequestedPageRef = useRef(null); // guard against double requests of same page

//   // Reset when `refresh` changes
//   useEffect(() => {
//     setReviews([]);
//     setPage(1);
//     setHasMore(true);
//     setError("");
//     lastRequestedPageRef.current = null;
//   }, [refresh]);

//   // Fetch current page
//   useEffect(() => {
//     // Don’t fetch if we already fetched this page
//     if (lastRequestedPageRef.current === page) return;
//     lastRequestedPageRef.current = page;

//     let cancelled = false;
//     const controller = new AbortController();

//     const fetchPage = async () => {
//       if (!hasMore) return;
//       setLoading(true);
//       setError("");

//       try {
//         // If you’re not using a dev proxy, replace with: "http://localhost:5001/api/reviews"
//         const { data } = await axios.get("http://localhost:5001/api/reviews", {
//           params: { page, limit: LIMIT },
//           signal: controller.signal,
//         });

//         const items = data?.items || [];
//         const nextHasMore = typeof data?.hasMore === "boolean" ? data.hasMore : items.length === LIMIT;

//         if (!cancelled) {
//           // De-dup *inside* setReviews using previous state
//           setReviews((prev) => {
//             if (items.length === 0) return prev;
//             const seen = new Set(prev.map((r) => r._id));
//             const merged = [...prev];
//             for (const r of items) {
//               if (!seen.has(r._id)) {
//                 merged.push(r);
//                 seen.add(r._id);
//               }
//             }
//             return merged;
//           });
//           setHasMore(nextHasMore);
//         }
//       } catch (e) {
//         if (!cancelled && e.name !== "CanceledError") {
//           setError("Failed to load reviews.");
//           setHasMore(false);
//         }
//       } finally {
//         if (!cancelled) setLoading(false);
//       }
//     };

//     fetchPage();
//     return () => {
//       cancelled = true;
//       controller.abort();
//     };
//   }, [page, hasMore]);

//   // IntersectionObserver to paginate
//   useEffect(() => {
//     if (!hasMore) return;
//     const rootEl = containerRef.current;
//     const target = sentinelRef.current;
//     if (!rootEl || !target) return;

//     const io = new IntersectionObserver(
//       ([entry]) => {
//         if (entry.isIntersecting) {
//           setPage((p) => p + 1);
//         }
//       },
//       { root: rootEl, rootMargin: "200px", threshold: 0.01 }
//     );

//     io.observe(target);
//     return () => io.disconnect();
//   }, [hasMore]);

//   const isInitial = reviews.length === 0 && loading;

//   return (
//     <div
//       ref={containerRef}
//       className="space-y-3 mt-10 max-w-xl mx-auto overflow-auto h-[65vh] border-[2px] rounded-lg p-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden bg-[#E7F7FC]  "
//       aria-busy={loading ? "true" : "false"}
//       aria-live="polite"
//     >
//       {isInitial && <Spinner label="Loading reviews..." />}

//       {!isInitial &&
//         reviews.map((r) => (
//           <article key={r._id} className="border border-gray-300 rounded-xl p-3 bg-white/80">
//             <div className="flex justify-between items-center">
//               <h4 className="text-gray-900 font-semibold">{r.name}</h4>
//               <span className="inline-flex items-center gap-1 text-indigo-600 font-medium">
//                 {Number(r.rating).toFixed(1)} <span aria-hidden>★</span>
//               </span>
//             </div>
//             <p className="text-gray-800 text-sm mt-1">{r.comment}</p>
//           </article>
//         ))}

//       {error && <div className="text-red-600 text-sm">{error}</div>}

//       {reviews.length > 0 && loading && <Spinner label="Loading more reviews..." />}

//       {!hasMore && reviews.length > 0 && !loading && (
//         <p className="text-center text-gray-600 text-sm">— You’ve reached the end —</p>
//       )}

//       {/* Sentinel that triggers loading the next page */}
//       <div ref={sentinelRef} className="h-1" />
//     </div>
//   );
// }













































// import { useEffect, useState } from "react";
// import axios from "axios";

// export default function ReviewList({ refresh }) {
//   const [reviews, setReviews] = useState([]);

//   useEffect(() => {
//     const fetchReviews = async () => {
//       try {
//         const { data } = await axios.get("http://localhost:5001/api/reviews");
//         setReviews(data);
//       } catch (err) {
//         console.error("Failed to fetch reviews:", err);
//       }
//     };
//     fetchReviews();
//   }, [refresh]);

//   return (
//     <div className="space-y-3 mt-10 max-w-xl mx-auto overflow-auto h-[65vh]   border-[2px] rounded-lg p-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden  "> {/* bg-[#E7F7FC] */}
//       {reviews.map((r) => (
//         <div key={r._id} className="border border-gray-300 rounded-xl p-3 bg-white/5 ">
//           <div className="flex justify-between items-center">
//             <h4 className="text-black font-semibold">{r.name}</h4>
//             <span className="text-blue-500">{r.rating} ★</span>
//           </div>
//           <p className="text-black text-sm">{r.comment}</p>
//         </div>
//       ))}
//     </div>
//   );
// }


















